/**
 * Converts string to URL-friendly slug
 * Example: "Home Page" -> "home-page"
 */
export const generateSlug = (text) => {
  if (!text) return "";
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "") // Remove special characters
    .replace(/\s+/g, "-") // Replace spaces with hyphens
    .replace(/-+/g, "-") // Replace multiple hyphens with single hyphen
    .replace(/^-+|-+$/g, ""); // Remove leading/trailing hyphens
};

/**
 * Find node and build full path from tree hierarchy
 * @param {number} nodeValue - Selected node value
 * @param {array} options - Tree options
 * @param {array} pathSegments - Accumulated path segments
 * @returns {boolean} - Whether node was found
 */
const findNodePath = (nodeValue, options, pathSegments = []) => {
  for (const option of options) {
    if (option.value === nodeValue) {
      // Found the node, add its label to path
      pathSegments.push(generateSlug(option.label));
      return true;
    }

    if (option.children && option.children.length > 0) {
      // Add current node to path and search in children
      pathSegments.push(generateSlug(option.label));

      if (findNodePath(nodeValue, option.children, pathSegments)) {
        // Node found in children, path is complete
        return true;
      }

      // Node not found in this branch, remove this node from path
      pathSegments.pop();
    }
  }

  return false;
};

/**
 * Get full path from parent hierarchy
 * @param {object} selectedParent - Selected parent node {label, value}
 * @param {array} allOptions - All tree options
 * @param {string} currentName - Current form name
 * @returns {string} - Generated path like "/settings/users/roles/home-page"
 */
export const generatePath = (selectedParent, allOptions, currentName) => {
  if (!currentName) return "";

  const currentSlug = generateSlug(currentName);

  if (!selectedParent) {
    return `/${currentSlug}`;
  }

  const pathSegments = [];

  // Find the node and build full path
  findNodePath(selectedParent.value, allOptions, pathSegments);

  // Add current name at the end
  pathSegments.push(currentSlug);

  return `/${pathSegments.join("/")}`;
};
